/**
 * Unified Command Palette Component (Cmd+K / Ctrl+K / ✨ FAB)
 * Enhanced with sections: Quick Actions, Videos, AI Help, Recent
 */

import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  Inject,
  Type
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { UnifiedSearchService } from '../core/unified-search.service';
import { FeatureDiscoveryService } from '../core/feature-discovery.service';
import { WorkflowPlayerService } from '../guide/workflow-player.service';
import { QuickActionsService } from '../core/quick-actions.service';
import { OnboardingChecklistService, OnboardingStep, OnboardingProgress } from '../core/onboarding-checklist.service';
import { OnboardingTier, VIDEO_BASE_URL } from '../core/assistant-actions.registry';
import { DialogService, DialogType } from '../../shared/dialog/dialog.service';
import { DialogConfig } from '../../shared/dialog/dialog-config';
import { TeamInboxComponent } from '../../shared/conversation/team-inbox/team-inbox.component';
import { DataShareService } from '../../../services/data-share.service';

import {
  UnifiedSearchResults,
  QuickActionResult,
  VideoResult,
  AIHelpResult,
  SuggestionResult,
  RecentResult
} from '../models/unified-results.models';
import { DiscoverableFeature } from '../models/feature-discovery.models';

@Component({
  selector: 'ai-unified-command-palette',
  templateUrl: './unified-command-palette.component.html',
  styleUrls: ['./unified-command-palette.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule]
})
export class UnifiedCommandPaletteComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  query = '';
  isLoading = false;
  isKnowledgeLoading = false;

  // Section results
  results: UnifiedSearchResults = {
    discovery: [],
    quickActions: [],
    videos: [],
    knowledge: null,
    aiHelp: null,
    suggestions: [],
    recent: [],
    routeName: ''
  };

  // Navigation
  selectedSection: 'discovery' | 'quick-actions' | 'videos' | 'ai-help' | 'suggestions' | 'recent' = 'discovery';
  selectedIndex = 0;

  // Expanded states
  expandedVideoId: string | null = null;
  expandedDiscoveryVideoId: string | null = null;
  expandedDiscoveryVideoUrl: SafeResourceUrl | null = null;
  readonly languages = [
    { code: 'en', nativeLabel: 'English', englishLabel: 'English' },
    { code: 'hi', nativeLabel: 'हिन्दी', englishLabel: 'Hindi' },
    { code: 'kn', nativeLabel: 'ಕನ್ನಡ', englishLabel: 'Kannada' },
    { code: 'ta', nativeLabel: 'தமிழ்', englishLabel: 'Tamil' },
    { code: 'mr', nativeLabel: 'मराठी', englishLabel: 'Marathi' },
    { code: 'te', nativeLabel: 'తెలుగు', englishLabel: 'Telugu' },
    { code: 'ml', nativeLabel: 'മലയാളം', englishLabel: 'Malayalam' }
  ];
  selectedLanguage = 'en';
  showRecentSection = false;

  // ═══════════════════════════════════════════════════════════════════════════
  // ONBOARDING / TRIAL MODE
  // ═══════════════════════════════════════════════════════════════════════════
  isTrialMode = false;
  onboardingProgress: OnboardingProgress | null = null;
  onboardingSteps: OnboardingStep[] = [];
  onboardingByTier: Record<OnboardingTier, OnboardingStep[]> = {
    critical: [],
    important: [],
    helpful: []
  };
  activeOnboardingStep: string | null = null;

  readonly tierConfig: Record<OnboardingTier, { label: string; icon: string; emoji: string }> = {
    critical: { label: 'Get Started', icon: 'rocket_launch', emoji: '🚀' },
    important: { label: 'Level Up', icon: 'star', emoji: '⭐' },
    helpful: { label: 'Pro Tips', icon: 'lightbulb', emoji: '💡' }
  };

  devWorkflows = [
    { id: 'bulk-upload-leads', label: 'Bulk Upload Leads' },
    { id: 'customize-grid-columns', label: 'Customize Grid Columns' },
    { id: 'leads-page-overview', label: 'Leads Page Overview' },
    { id: 'leads-select-all-actions', label: 'Select All Leads' },
    { id: 'leads-assign', label: 'Assign Leads' },
    { id: 'leads-start-whatsapp', label: 'Start WhatsApp' },
    { id: 'leads-filter', label: 'Filter Leads' },
    { id: 'leads-delete', label: 'Delete / Do Not Track Leads' },
    { id: 'leads-edit-multiple-deals', label: 'Edit Multiple Deals' },
    { id: 'leads-hide-columns', label: 'Hide Columns' },
    { id: 'leads-column-filter-panel', label: 'Filter Columns in Panel' },
    { id: 'leads-column-chart', label: 'Column Chart' },
    { id: 'leads-column-search', label: 'Search Column' },
    { id: 'leads-smart-views', label: 'Save Smart Views' },
    { id: 'leads-manual-close', label: 'Manual Close Leads' },
    { id: 'leads-distribution-automation', label: 'Create & Attach Distributions' },
    { id: 'leads-export', label: 'Export Leads' },
    { id: 'user-management-overview', label: 'User Mgmt Overview' },
    { id: 'user-invite', label: 'Invite User' },
    { id: 'user-remove-or-inactivate', label: 'Remove / Inactivate User' },
    { id: 'user-change-registered-number', label: 'Change Registered Number' },
    { id: 'team-create', label: 'Create Team' },
    { id: 'team-membership-manage', label: 'Manage Team Members' },
    { id: 'user-edit-name-role', label: 'Edit User Name & Role' }
  ];

  // User context
  private activeAccount: any = null;
  private supportChannelId: string | null = null;

  hasSearched = false;
  private lastSearchedQuery = '';
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private unifiedSearch: UnifiedSearchService,
    private discoveryService: FeatureDiscoveryService,
    private workflowPlayer: WorkflowPlayerService,
    private dialogService: DialogService,
    private dataService: DataShareService,
    private quickActionsService: QuickActionsService,
    private onboardingService: OnboardingChecklistService,
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    // Search is now user-triggered (Search button or Enter key)

    // Get active account for support chat and trial mode detection
    this.dataService.getActiveAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: any) => {
        this.quickActionsService.setActiveAccount(account || null);
        this.activeAccount = account || null;
        if (account) {
          this.supportChannelId = `${account.accountId}__SALESMAX_SUPPORT_ALL`;
          // Set trial mode based on account type
          this.isTrialMode = account.account?.accountType === 'TEAM_TRIAL';
          console.log('[Command Palette] Account type:', account.account?.accountType, '| isTrialMode:', this.isTrialMode);

          // Reload onboarding steps based on trial mode
          this.loadOnboardingSteps();
        } else {
          this.supportChannelId = null;
          this.isTrialMode = false;
        }
      });

    // Show default results
    this.showDefaultResults();

    // Initialize onboarding
    this.initializeOnboarding();
  }

  private initializeOnboarding(): void {
    // Subscribe to onboarding progress
    this.onboardingService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(progress => {
        this.onboardingProgress = progress;
        console.log('[Command Palette] Onboarding progress:', progress);
      });

    // Load onboarding steps (will be reloaded when account is received)
    this.loadOnboardingSteps();

    // Watch for workflow completion to refresh status
    this.workflowPlayer.activeSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        if (session?.status === 'completed') {
          this.onboardingService.refreshCompletionStatus();
          this.activeOnboardingStep = null;
        }
      });
  }

  /**
   * Load onboarding steps based on trial mode
   * In trial mode, only show trial-specific steps for quick value
   */
  private loadOnboardingSteps(): void {
    if (this.isTrialMode) {
      this.onboardingSteps = this.onboardingService.getTrialSteps();
      // Group trial steps by tier
      const trialSteps = this.onboardingSteps;
      this.onboardingByTier = {
        critical: trialSteps.filter(s => s.config.tier === 'critical'),
        important: trialSteps.filter(s => s.config.tier === 'important'),
        helpful: trialSteps.filter(s => s.config.tier === 'helpful')
      };
      console.log('[Command Palette] Loaded trial steps:', this.onboardingSteps.length);
    } else {
      this.onboardingSteps = this.onboardingService.getAllOnboardingSteps();
      this.onboardingByTier = this.onboardingService.getStepsByTier();
      console.log('[Command Palette] Loaded all onboarding steps:', this.onboardingSteps.length);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.setBodyScrollLocked(false);
  }

  // ============================================================
  // KEYBOARD SHORTCUTS
  // ============================================================

  @HostListener('document:open-command-palette', ['$event'])
  handleOpenCommandPalette(event: CustomEvent): void {
    const query = event.detail?.query || '';
    this.openWithQuery(query);
  }

  openWithQuery(query: string): void {
    if (!query) {
      this.open();
      return;
    }

    this.isOpen = true;
    this.query = query;
    this.selectedIndex = 0;
    this.selectedSection = 'quick-actions';
    this.expandedVideoId = null;
    this.expandedDiscoveryVideoId = null;
    this.expandedDiscoveryVideoUrl = null;
    this.setBodyScrollLocked(true);

    this.onboardingService.refreshCompletionStatus();
    this.loadOnboardingSteps();

    // Trigger search immediately with the pre-filled query
    this.triggerSearch();

    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Cmd+K or Ctrl+K to toggle
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }

    // Only handle other keys when open
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.selectNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectPrevious();
        break;

      case 'Enter':
        event.preventDefault();
        if (!this.hasSearched && this.query.trim().length >= 3) {
          this.triggerSearch();
        } else {
          this.executeSelected();
        }
        break;

      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          this.selectPreviousSection();
        } else {
          this.selectNextSection();
        }
        break;

      case 'ArrowRight':
        if (this.selectedSection === 'videos') {
          event.preventDefault();
          this.toggleVideoExpand();
        }
        break;

      case 'ArrowLeft':
        if (this.expandedVideoId) {
          event.preventDefault();
          this.expandedVideoId = null;
        }
        break;
    }
  }

  // ============================================================
  // OPEN/CLOSE
  // ============================================================

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.isOpen = true;
    this.query = '';
    this.selectedIndex = 0;
    this.selectedSection = 'quick-actions';
    this.expandedVideoId = null;
    this.expandedDiscoveryVideoId = null;
    this.expandedDiscoveryVideoUrl = null;
    this.hasSearched = false;
    this.lastSearchedQuery = '';
    this.showDefaultResults();
    this.setBodyScrollLocked(true);

    // Refresh onboarding status to capture any actions taken elsewhere in the app
    this.onboardingService.refreshCompletionStatus();
    this.loadOnboardingSteps();

    // Focus input after render
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  close(): void {
    this.isOpen = false;
    this.query = '';
    this.expandedVideoId = null;
    this.expandedDiscoveryVideoId = null;
    this.expandedDiscoveryVideoUrl = null;
    this.hasSearched = false;
    this.lastSearchedQuery = '';
    this.selectedIndex = 0;
    this.setBodyScrollLocked(false);
  }

  // ============================================================
  // SEARCH
  // ============================================================

  clearQuery(): void {
    this.query = '';
    this.onQueryChange('');
    this.searchInput?.nativeElement?.focus();
  }

  onQueryChange(query: string): void {
    this.query = query;
    this.selectedIndex = 0;

    // Reset search state when query changes so Search button reappears
    if (query.trim() !== this.lastSearchedQuery) {
      this.hasSearched = false;
    }

    // Show default results when query is cleared
    if (!query.trim()) {
      this.hasSearched = false;
      this.lastSearchedQuery = '';
      this.showDefaultResults();
    }
  }

  triggerSearch(): void {
    const trimmed = this.query.trim();
    if (trimmed.length < 3) return;

    this.lastSearchedQuery = trimmed;
    this.hasSearched = true;
    this.performSearch(trimmed);
  }

  refineSearch(text: string): void {
    this.query = text;
    this.selectedIndex = 0;
    this.performSearch(text);

    // Move caret to end
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
      this.searchInput?.nativeElement?.setSelectionRange(text.length, text.length);
    }, 0);
  }

  private async performSearch(query: string): Promise<void> {
    this.isLoading = true;
    this.isKnowledgeLoading = true;

    const context = {
      currentRoute: this.router.url,
      accountId: this.activeAccount?.accountId || '',
      userId: this.activeAccount?.userId || ''
    };

    // Fire BOTH at the same time — don't await either yet
    const searchPromise = this.unifiedSearch.search(query, context);
    const knowledgePromise = this.unifiedSearch.searchKnowledge(query, context.accountId);

    // Phase 1: render quick actions as soon as they arrive
    searchPromise.then(results => {
      this.results = results;
      this.isLoading = false;
      this.autoSelectSection();
    }).catch(error => {
      console.error('[Command Palette] Search failed:', error);
      this.isLoading = false;
    });

    // Phase 2: fill in knowledge when RAG returns (independently)
    knowledgePromise.then(knowledge => {
      this.results = { ...this.results, knowledge };
      this.isKnowledgeLoading = false;
    }).catch(() => {
      this.isKnowledgeLoading = false;
    });
  }

  private async showDefaultResults(): Promise<void> {
    await this.performSearch('');
  }

  // ============================================================
  // SECTION NAVIGATION
  // ============================================================

  private autoSelectSection(): void {
    if (this.results.discovery.length > 0) {
      this.selectedSection = 'discovery';
    } else if (this.results.quickActions.length > 0) {
      this.selectedSection = 'quick-actions';
    } else if (this.results.videos.length > 0) {
      this.selectedSection = 'videos';
    } else if (this.results.aiHelp) {
      this.selectedSection = 'ai-help';
    } else if (this.results.suggestions.length > 0) {
      this.selectedSection = 'suggestions';
    } else if (this.results.recent.length > 0) {
      this.selectedSection = 'recent';
    }
  }

  private selectNextSection(): void {
    const sections: Array<'discovery' | 'quick-actions' | 'videos' | 'ai-help' | 'suggestions' | 'recent'> = [
      'discovery',
      'quick-actions',
      'videos',
      'ai-help',
      'suggestions',
      'recent'
    ];

    const currentIndex = sections.indexOf(this.selectedSection);
    const nextIndex = (currentIndex + 1) % sections.length;
    this.selectedSection = sections[nextIndex];
    this.selectedIndex = 0;
  }

  private selectPreviousSection(): void {
    const sections: Array<'discovery' | 'quick-actions' | 'videos' | 'ai-help' | 'suggestions' | 'recent'> = [
      'discovery',
      'quick-actions',
      'videos',
      'ai-help',
      'suggestions',
      'recent'
    ];

    const currentIndex = sections.indexOf(this.selectedSection);
    const prevIndex = currentIndex === 0 ? sections.length - 1 : currentIndex - 1;
    this.selectedSection = sections[prevIndex];
    this.selectedIndex = 0;
  }

  selectNext(): void {
    const maxIndex = this.getCurrentSectionLength() - 1;
    if (maxIndex < 0) return;

    this.selectedIndex = this.selectedIndex >= maxIndex ? 0 : this.selectedIndex + 1;
    this.scrollToSelected();
  }

  selectPrevious(): void {
    const maxIndex = this.getCurrentSectionLength() - 1;
    if (maxIndex < 0) return;

    this.selectedIndex = this.selectedIndex === 0 ? maxIndex : this.selectedIndex - 1;
    this.scrollToSelected();
  }

  private getCurrentSectionLength(): number {
    switch (this.selectedSection) {
      case 'discovery':
        return this.results.discovery.length;
      case 'quick-actions':
        return this.results.quickActions.length;
      case 'videos':
        return this.results.videos.length;
      case 'ai-help':
        return this.results.aiHelp ? 1 : 0;
      case 'suggestions':
        return this.results.suggestions.length;
      case 'recent':
        return this.results.recent.length;
      default:
        return 0;
    }
  }

  selectItem(section: string, index: number): void {
    this.selectedSection = section as any;
    this.selectedIndex = index;
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const element = this.document.querySelector('.palette-item.selected');
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 10);
  }

  // ============================================================
  // EXECUTION
  // ============================================================

  async executeSelected(): Promise<void> {
    switch (this.selectedSection) {
      case 'discovery':
        if (this.results.discovery[this.selectedIndex]) {
          await this.executeDiscovery(this.results.discovery[this.selectedIndex]);
        }
        break;
      case 'quick-actions':
        if (this.results.quickActions[this.selectedIndex]) {
          await this.executeQuickAction(this.results.quickActions[this.selectedIndex]);
        }
        break;
      case 'videos':
        if (this.results.videos[this.selectedIndex]) {
          this.playVideo(this.results.videos[this.selectedIndex]);
        }
        break;
      case 'ai-help':
        if (this.results.aiHelp && this.results.aiHelp.actions[this.selectedIndex]) {
          await this.executeAIAction(this.results.aiHelp.actions[this.selectedIndex]);
        }
        break;
      case 'suggestions':
        if (this.results.suggestions[this.selectedIndex]) {
          this.executeSuggestion(this.results.suggestions[this.selectedIndex]);
        }
        break;
      case 'recent':
        if (this.results.recent[this.selectedIndex]) {
          await this.executeRecent(this.results.recent[this.selectedIndex]);
        }
        break;
    }
  }

  async executeQuickAction(action: QuickActionResult): Promise<void> {
    console.log('[Command Palette] Executing quick action:', action);

    // Add to recent
    this.unifiedSearch.addToRecent(action);

    try {
      // CONTEXTUAL ACTION - Has step-by-step guidance
      if (action.steps && action.steps.length > 0) {
        // Navigate to the required page first
        if (action.route) {
          await this.router.navigateByUrl(action.route);
        }

        // Launch workflow player if workflowId exists (preferred)
        if (action.workflowId) {
          console.log('[Command Palette] Launching workflow:', action.workflowId);
          await this.workflowPlayer.startWorkflow(action.workflowId);
          this.close();
        } else {
          // Show step-by-step guidance panel
          console.log('[Command Palette] Showing step guidance:', action.steps);
          this.showStepsGuidance(action.title, action.steps);
          this.close();
        }
      }
      // DIRECT ACTION - Component-based (open dialog)
      else if (action.component) {
        this.openQuickActionComponent(action);
        this.close();
      }
      // NAVIGATION ACTION - Route-based
      else if (action.route) {
        await this.router.navigateByUrl(action.route);
        this.close();
      }
      // CUSTOM ACTION - Function-based
      else if (action.action) {
        await action.action();
        this.close();
      }
    } catch (error) {
      console.error('[Command Palette] Failed to execute action:', error);
    }
  }

  /**
   * Show step-by-step guidance panel for contextual actions
   *
   * TODO: Implement UI component to display steps panel
   * This could be:
   * - A toast/snackbar with step list
   * - A sidebar panel with numbered steps
   * - An overlay tooltip following the user
   *
   * For now, logs to console so developers can see the guidance
   */
  private showStepsGuidance(title: string, steps: string[]): void {
    console.log(`[Step Guidance] ${title}`);
    steps.forEach((step, index) => {
      console.log(`  ${index + 1}. ${step}`);
    });

    // TODO: Replace with actual UI display
    // Example implementation could emit via a dedicated BehaviorSubject:
    // this.stepsGuidanceSubject.next({ title, steps, timestamp: new Date() });

    // Or show a simple alert for now (replace with better UI later)
    alert(`${title}\n\n${steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`);
  }

  playVideo(video: VideoResult): void {
    console.log('[Command Palette] Playing video:', video);

    // Toggle expand
    if (this.expandedVideoId === video.id) {
      this.expandedVideoId = null;
    } else {
      this.expandedVideoId = video.id;
      this.unifiedSearch.addToRecent(video);
    }
  }

  toggleDiscoveryVideo(feature: DiscoverableFeature): void {
    if (this.expandedDiscoveryVideoId === feature.id) {
      this.expandedDiscoveryVideoId = null;
      this.expandedDiscoveryVideoUrl = null;
    } else {
      this.expandedDiscoveryVideoId = feature.id;
      this.expandedDiscoveryVideoUrl = this.getSafeVideoUrl(feature.videoUrl);
      this.discoveryService.markDiscovered(feature.id);
    }
  }

  private toggleVideoExpand(): void {
    const video = this.results.videos[this.selectedIndex];
    if (video) {
      this.playVideo(video);
    }
  }

  private openQuickActionComponent(action: {
    component?: Type<any>;
    dialogType?: DialogType;
    closeOnClickOutside?: boolean;
    dialogConfig?: DialogConfig;
  }): void {
    if (!action.component) {
      return;
    }

    const dialogType = action.dialogType ?? DialogType.SIDE_MODAL;
    const closeOnClickOutside = action.closeOnClickOutside ?? true;
    this.dialogService.open(
      action.component,
      action.dialogConfig ?? {},
      dialogType,
      closeOnClickOutside
    );
  }

  getSafeVideoUrl(url?: string): SafeResourceUrl {
    if (!url) return this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    const fullUrl = url.startsWith('http') ? url : `${VIDEO_BASE_URL}/${url}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
  }

  async executeAIAction(action: any): Promise<void> {
    console.log('[Command Palette] Executing AI action:', action);
    try {
      if (action?.type === 'workflow-replay' && action.workflowId) {
        await this.workflowPlayer.startWorkflow(action.workflowId);
        this.close();
        return;
      }

      await action.action();
      this.close();
    } catch (error) {
      console.error('[Command Palette] Failed to execute AI action:', error);
    }
  }

  async executeSuggestion(suggestion: SuggestionResult): Promise<void> {
    console.log('[Command Palette] Executing suggestion:', suggestion);

    const suggestedQuery = suggestion.text?.trim();
    if (!suggestedQuery) {
      suggestion.action?.();
      return;
    }

    this.query = suggestedQuery;
    this.selectedIndex = 0;
    this.selectedSection = 'discovery';

    // Move caret to end so user sees applied suggestion
    setTimeout(() => {
      this.searchInput?.nativeElement?.setSelectionRange(
        suggestedQuery.length,
        suggestedQuery.length
      );
    }, 0);

    await this.performSearch(suggestedQuery);

    suggestion.action?.();
  }

  onLanguageChange(code: string): void {
    this.selectedLanguage = code;
    // TODO: integrate with multilingual search once backend is ready
  }

  async startDevWorkflow(id: string): Promise<void> {
    try {
      await this.workflowPlayer.startWorkflow(id);
      this.close();
    } catch (error) {
      console.error('[Command Palette] Failed to start dev workflow:', id, error);
    }
  }

  async executeRecent(recent: RecentResult): Promise<void> {
    console.log('[Command Palette] Executing recent:', recent);

    if (recent.origin === 'quick-action') {
      const quickAction = this.quickActionsService.getAction(recent.id);
      if (quickAction?.component) {
        this.openQuickActionComponent(quickAction);
        this.close();
        return;
      }

      const targetRoute = quickAction?.route || quickAction?.rootRoute;
      if (targetRoute) {
        await this.router.navigateByUrl(targetRoute);
        this.close();
        return;
      }
    }

    if (recent.route) {
      await this.router.navigateByUrl(recent.route);
      this.close();
    } else if (recent.action) {
      await recent.action();
      this.close();
    }
  }

  // ============================================================
  // DISCOVERY
  // ============================================================

  async executeDiscovery(feature: DiscoverableFeature, mode?: 'video' | 'workflow'): Promise<void> {
    console.log('[Command Palette] Executing discovery feature:', feature, 'mode:', mode);

    // Mark as discovered
    this.discoveryService.markDiscovered(feature.id);

    try {
      // Execute based on available actions
      if (mode !== 'video' && feature.workflowId) {
        // Launch workflow (Show Me)
        this.workflowPlayer.startWorkflow(feature.workflowId);
        this.close();
      } else if (feature.videoUrl) {
        // Play video (Watch) - convert to VideoResult and use existing playVideo
        const videoResult: VideoResult = {
          type: 'video',
          id: feature.id,
          title: feature.title,
          description: feature.description,
          duration: '0:00', // Unknown duration
          webVideoLink: this.getSafeVideoUrl(feature.videoUrl),
          appVideoLink: feature.videoUrl ? this.getSafeVideoUrl(feature.videoUrl) : undefined
        };
        this.playVideo(videoResult);
      } else if (feature.actionId) {
        // Execute action directly (Try It)
        // TODO: Implement action execution via ActionExecutorService
        console.log('[Discovery] Action execution not yet implemented:', feature.actionId);
        this.close();
      } else {
        // No action defined, just mark as discovered
        this.close();
      }
    } catch (error) {
      console.error('[Command Palette] Failed to execute discovery feature:', error);
    }
  }

  // ============================================================
  // SUPPORT
  // ============================================================

  openSupportChat(): void {
    if (!this.supportChannelId) {
      console.warn('[Command Palette] Support channel not available');
      return;
    }

    this.dialogService.open(TeamInboxComponent, {
      data: { selectedChannelId: this.supportChannelId, viewMode: 'dialog-mode' }
    }, 1);

    this.close();
  }

  // ============================================================
  // HELPERS
  // ============================================================

  get hasResults(): boolean {
    // Quick actions are now always in sidebar, so don't count them for main content
    return this.results.discovery.length > 0 ||
           this.results.videos.length > 0 ||
           this.results.knowledge !== null ||
           this.results.aiHelp !== null ||
           this.results.suggestions.length > 0 ||
           this.results.recent.length > 0;
  }

  trackById(index: number, item: any): string {
    return item.id;
  }

  private setBodyScrollLocked(locked: boolean): void {
    const body = this.document?.body;
    if (!body) {
      return;
    }

    if (locked) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
  }

  // ============================================================
  // ONBOARDING METHODS
  // ============================================================

  get onboardingPercent(): number {
    return this.onboardingProgress?.percentComplete ?? 0;
  }

  get onboardingProgressText(): string {
    if (!this.onboardingProgress) return '';
    return `${this.onboardingProgress.completedSteps}/${this.onboardingProgress.totalSteps}`;
  }

  get visibleTiers(): OnboardingTier[] {
    return ['critical', 'important', 'helpful'];
  }

  getStepsForTier(tier: OnboardingTier): OnboardingStep[] {
    return this.onboardingByTier[tier] || [];
  }

  isStepCompleted(step: OnboardingStep): boolean {
    return this.onboardingService.isCompleted(step.actionId);
  }

  isStepActive(step: OnboardingStep): boolean {
    return this.activeOnboardingStep === step.actionId;
  }

  getTierProgress(tier: OnboardingTier): { completed: number; total: number } {
    return this.onboardingService.getTierProgress(tier);
  }

  isTierComplete(tier: OnboardingTier): boolean {
    const { completed, total } = this.getTierProgress(tier);
    return completed === total && total > 0;
  }

  getStepTitle(step: OnboardingStep): string {
    return step.config.title || step.action.text;
  }

  getStepDescription(step: OnboardingStep): string {
    return step.config.description || step.action.tooltip;
  }

  getStepCta(step: OnboardingStep): string {
    return step.config.ctaLabel || 'Start';
  }

  getStepIcon(step: OnboardingStep): string {
    return step.action.icon;
  }

  async startOnboardingStep(step: OnboardingStep): Promise<void> {
    const action = step.action;
    this.activeOnboardingStep = step.actionId;

    // Priority: workflow > component > route
    if (action.workflowId) {
      await this.workflowPlayer.startWorkflow(action.workflowId);
      this.close();
    } else if (action.component) {
      this.dialogService.open(
        action.component,
        action.dialogConfig || {},
        action.dialogType,
        action.closeOnClickOutside ?? true
      );
      this.close();
    } else if (action.route) {
      await this.router.navigateByUrl(action.route);
      this.close();
    }
  }

  skipOnboardingStep(step: OnboardingStep, event: Event): void {
    event.stopPropagation();
    this.onboardingService.skipStep(step.actionId);
  }

  trackByStepId(index: number, step: OnboardingStep): string {
    return step.actionId;
  }
}
